import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, Target, ArrowUp, ArrowDown, Sparkles, FileText, RefreshCw } from 'lucide-react';
import { EduKnowledgePoint, EduSubject, EduDomain, CreateKnowledgePointRequest, UpdateKnowledgePointRequest } from '../../../types/learning';
import { API_CONFIG } from '../../../config/api';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const KnowledgePointManagement: React.FC = () => {
  const [knowledgePoints, setKnowledgePoints] = useState<EduKnowledgePoint[]>([]);
  const [subjects, setSubjects] = useState<EduSubject[]>([]);
  const [domains, setDomains] = useState<EduDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [articleFilter, setArticleFilter] = useState('all'); // 新增文章筛选器
  const [showModal, setShowModal] = useState(false);
  const [editingPoint, setEditingPoint] = useState<EduKnowledgePoint | null>(null);
  const [generatingContent, setGeneratingContent] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [formData, setFormData] = useState<CreateKnowledgePointRequest>({
    subject_id: '',
    name: '',
    description: '',
    order_num: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  // 当领域选择变化时重新获取学科数据
  useEffect(() => {
    if (domainFilter) {
      setSubjectsLoading(true);
      fetchSubjects(domainFilter).finally(() => setSubjectsLoading(false));
    }
  }, [domainFilter]);

  // 当搜索条件变化时重新获取数据
  useEffect(() => {
    // 只有在 subjects 数据已经加载完成后才执行查询
    if (subjects.length === 0 && domainFilter !== 'all') return;
    
    const timeoutId = setTimeout(() => {
      fetchKnowledgePoints();
    }, 300); // 防抖300ms
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, domainFilter, subjectFilter, statusFilter, articleFilter, subjects]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // 先加载基础数据（领域和学科），再加载知识点
      await Promise.all([fetchSubjects(), fetchDomains()]);
      await fetchKnowledgePoints();
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchData();
      toast.success('数据刷新成功');
    } catch (error) {
      console.error('刷新失败:', error);
      toast.error('刷新失败');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchKnowledgePoints = async () => {
    try {
      // 构建查询参数
      const params = new URLSearchParams({
        action: 'list',
        limit: '100'
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      if (subjectFilter !== 'all') {
        params.append('subject_id', subjectFilter);
      } else if (domainFilter !== 'all') {
        // 如果选择了领域但没有选择具体学科，则查询该领域下所有学科的知识点
        const subjectIds = subjects.map(s => s.id);
        if (subjectIds.length > 0) {
          params.append('subject_ids', subjectIds.join(','));
        }
      }
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter === 'active' ? '1' : '0');
      }
      
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-knowledge-admin-manage?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch knowledge points');

      const result = await response.json();
      if (result.success) {
        setKnowledgePoints(result.data.data || []);
      }
    } catch (error) {
      console.error('获取知识点列表失败:', error);
      toast.error('获取知识点列表失败');
    }
  };

  const fetchSubjects = async (domainId?: string) => {
    try {
      // 构建查询参数
      const params = new URLSearchParams({
        action: 'list',
        limit: '100'
      });
      
      // 如果指定了领域ID，则只获取该领域下的学科
      if (domainId && domainId !== 'all') {
        params.append('domain_id', domainId);
      }
      
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch subjects');

      const result = await response.json();
      if (result.success) {
        setSubjects(result.data.data || []);
      }
    } catch (error) {
      console.error('获取学科列表失败:', error);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-domains-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch domains');

      const result = await response.json();
      if (result.success) {
        setDomains(result.data.data || []);
      }
    } catch (error) {
      console.error('获取领域列表失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingPoint(null);
    setFormData({
      subject_id: '',
      name: '',
      description: '',
      order_num: knowledgePoints.length
    });
    setShowModal(true);
  };

  const handleEdit = (point: EduKnowledgePoint) => {
    setEditingPoint(point);
    setFormData({
      subject_id: point.subject_id,
      name: point.name,
      description: point.description || '',
      order_num: point.order_num
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('请输入知识点名称');
      return;
    }

    if (!formData.subject_id) {
      toast.error('请选择所属学科');
      return;
    }

    try {
      const url = editingPoint 
        ? `${API_CONFIG.FUNCTIONS_URL}/edu-knowledge-admin-manage?action=update`
        : `${API_CONFIG.FUNCTIONS_URL}/edu-knowledge-admin-manage?action=create`;
      
      const body = editingPoint 
        ? { ...formData, id: editingPoint.id } as UpdateKnowledgePointRequest
        : formData;

      const response = await fetch(url, {
        method: editingPoint ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Failed to save knowledge point');

      const result = await response.json();
      if (result.success) {
        toast.success(editingPoint ? '知识点更新成功' : '知识点创建成功');
        setShowModal(false);
        await fetchKnowledgePoints();
      } else {
        throw new Error(result.error || 'Save failed');
      }
    } catch (error) {
      console.error('保存知识点失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    }
  };

  const handleDelete = async (point: EduKnowledgePoint) => {
    if (!confirm(`确定要删除知识点「${point.name}」吗？此操作不可撤销。`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-knowledge-admin-manage?action=delete&id=${point.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to delete knowledge point');

      const result = await response.json();
      if (result.success) {
        toast.success('知识点删除成功');
        await fetchKnowledgePoints();
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      console.error('删除知识点失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleGenerateArticle = async (point: EduKnowledgePoint) => {
    const confirmGenerate = confirm(`确定要为知识点「${point.name}」生成AI文章吗？\n\n如果该知识点已有文章，将会被覆盖更新。\n\n这可能需要几分钟时间。`);
    if (!confirmGenerate) return;

    try {
      setGeneratingContent(point.id);
      
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/aimodule-content-admin-generate-edu?type=knowledge_article`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          knowledge_point_id: point.id,
          knowledge_point_name: point.name,
          subject_name: getSubjectName(point.subject_id),
          domain_name: getDomainName(point.subject_id),
          description: point.description || '',
          update_existing: true
        })
      });

      if (!response.ok) throw new Error('Failed to generate article');

      const result = await response.json();
      if (result.success) {
        toast.success('AI文章生成/更新成功！');
        await fetchKnowledgePoints();
      } else {
        throw new Error(result.error || 'Generate failed');
      }
    } catch (error) {
      console.error('AI文章生成失败:', error);
      toast.error('生成失败：' + (error as Error).message);
    } finally {
      setGeneratingContent(null);
    }
  };

  const handleToggleStatus = async (point: EduKnowledgePoint) => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-knowledge-admin-manage?action=update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: point.id,
          status: point.status === 1 ? 0 : 1
        })
      });

      if (!response.ok) throw new Error('Failed to toggle status');

      const result = await response.json();
      if (result.success) {
        toast.success(`知识点已${point.status === 1 ? '禁用' : '启用'}`);
        await fetchKnowledgePoints();
      }
    } catch (error) {
      console.error('切换状态失败:', error);
      toast.error('操作失败');
    }
  };

  const getSubjectName = (subjectId: string) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject?.name || '未知学科';
  };

  const getDomainName = (subjectId: string) => {
    const subject = subjects.find(s => s.id === subjectId);
    if (!subject) return '未知领域';
    const domain = domains.find(d => d.id === subject.domain_id);
    return domain?.name || '未知领域';
  };

  // 现在直接使用从后端获取的学科数据，无需前端过滤

  // 根据文章筛选条件过滤知识点
  const filteredKnowledgePoints = knowledgePoints.filter(point => {
    if (articleFilter === 'all') return true;
    if (articleFilter === 'has_article') return point.has_article === true;
    if (articleFilter === 'no_article') return point.has_article !== true;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Target className="w-6 h-6 mr-2 text-blue-600" />
              知识点管理
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center px-3 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="刷新数据"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                刷新
              </button>
              <button
                onClick={handleCreate}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                添加知识点
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索知识点..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={domainFilter}
              onChange={(e) => {
                setDomainFilter(e.target.value);
                setSubjectFilter('all');
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部领域</option>
              {domains.map(domain => (
                <option key={domain.id} value={domain.id}>{domain.name}</option>
              ))}
            </select>
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              disabled={subjectsLoading}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="all">{subjectsLoading ? '加载中...' : '全部学科'}</option>
              {!subjectsLoading && subjects.map(subject => (
                <option key={subject.id} value={subject.id}>{subject.name}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="active">已启用</option>
              <option value="inactive">已禁用</option>
            </select>
            <select
              value={articleFilter}
              onChange={(e) => setArticleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部文章状态</option>
              <option value="has_article">已生成文章</option>
              <option value="no_article">未生成文章</option>
            </select>
          </div>

          {/* 知识点列表 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    知识点信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    所属学科
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    所属领域
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    是否生成文章
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    排序
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredKnowledgePoints.map((point) => (
                  <tr key={point.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap" style={{ maxWidth: '200px' }}>
                      <div>
                        <div className="text-sm font-medium text-gray-900 truncate" title={point.name}>
                          {point.name}
                        </div>
                        {point.description && (
                          <div className="text-sm text-gray-500 truncate" title={point.description}>
                            {point.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getSubjectName(point.subject_id)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getDomainName(point.subject_id)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {point.has_article ? (
                          <div className="flex items-center text-green-600">
                            <FileText className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">已生成</span>
                            {point.article_count && point.article_count > 1 && (
                              <span className="ml-1 text-xs text-gray-500">({point.article_count}篇)</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center text-gray-400">
                            <FileText className="w-4 h-4 mr-1" />
                            <span className="text-sm">未生成</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-900">{point.order_num}</span>
                        <div className="flex flex-col">
                          <button className="text-gray-400 hover:text-gray-600">
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600">
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(point)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          point.status === 1
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {point.status === 1 ? (
                          <><Eye className="w-3 h-3 mr-1" />启用</>
                        ) : (
                          <><EyeOff className="w-3 h-3 mr-1" />禁用</>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(point.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleGenerateArticle(point)}
                          disabled={generatingContent === point.id}
                          className="text-purple-600 hover:text-purple-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="AI生成文章"
                        >
                          <Sparkles className={`w-4 h-4 ${generatingContent === point.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => handleEdit(point)}
                          className="text-blue-600 hover:text-blue-900"
                          title="编辑"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(point)}
                          className="text-red-600 hover:text-red-900"
                          title="删除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredKnowledgePoints.length === 0 && (
            <div className="text-center py-12">
              <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无知识点数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 创建/编辑模态框 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {editingPoint ? '编辑知识点' : '创建知识点'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  思维类型 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.subject_id}
                  onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">请选择学科</option>
                  {subjects.filter(s => s.status === 1).map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {getDomainName(subject.id)} - {subject.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  知识点名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入知识点名称"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入知识点描述"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  排序序号
                </label>
                <input
                  type="number"
                  value={formData.order_num}
                  onChange={(e) => setFormData({ ...formData, order_num: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingPoint ? '更新' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgePointManagement;